import { useEffect, useMemo, useRef, useState } from 'react';
import { Input, InputWrapper, Button } from '../../../../components';
import { AddressForm } from '../../../forms/address/AddressForm';
import { useAddressFormState } from '../../../forms/address/hooks';
import { toAddress } from '../../../forms/address/utils';
import { useTranslation } from '../../../../providers';
import type { Address } from '../../../../graphql/generated/graphql';
import type { EditorProps } from '../types/editor-props';

// ── Value shape ───────────────────────────────────────────────────────────────

export interface PartnerValue {
  name: string;
  companyId: string;
  taxId: string;
  vatId: string;
  address: Address | null;
  email: string;
  phone: string;
}

function isPartnerValue(v: unknown): v is PartnerValue {
  return typeof v === 'object' && v !== null && 'name' in v;
}

const EMPTY: PartnerValue = {
  name: '',
  companyId: '',
  taxId: '',
  vatId: '',
  address: null,
  email: '',
  phone: ''
};

// ── Component ─────────────────────────────────────────────────────────────────

interface PartnerInlineEditorProps extends EditorProps {
  /** Catalog entry id: 'supplier' | 'customer'. Drives data-hover-field prefixes. */
  fieldId?: string;
}

// Maps field key → invoice GraphQL flat field path suffix per party prefix.
// e.g. prefix='supplier' → { name: 'supplierName', email: 'supplierContact.email', ... }
function hoverField(prefix: string, key: string): string {
  if (key === 'email') return `${prefix}Contact.email`;
  if (key === 'phone') return `${prefix}Contact.phone`;
  // name → supplierName, companyId → supplierCompanyId, etc.
  return `${prefix}${key.charAt(0).toUpperCase()}${key.slice(1)}`;
}

/** Inline editor for all partner (supplier / customer) fields rendered in-place. */
export function PartnerInlineEditor({
  value,
  onChange,
  onCommit,
  onCancel,
  errors,
  onRegisterSave,
  onDraftChange,
  fieldId
}: PartnerInlineEditorProps) {
  const prefix = fieldId ?? 'supplier';
  const t = useTranslation('modules.modals.supplier.fields');
  const tActions = useTranslation('modules.modals.supplier.actions');
  const tSections = useTranslation('modules.modals.supplier.sections');

  const partner = isPartnerValue(value) ? value : EMPTY;
  const [draft, setDraft] = useState<PartnerValue>(partner);
  const [saving, setSaving] = useState(false);
  // Track the address value fed into addressState so we can detect external changes.
  const [addressValue, setAddressValue] = useState<Address | null>(partner.address);

  // Sync when external value changes (e.g. after save round-trip).
  useEffect(() => {
    if (isPartnerValue(value)) {
      setDraft(value);
      setAddressValue(value.address);
    }
  }, [value]);

  const addressState = useAddressFormState({ open: true, value: addressValue });

  // Keep addressState in sync with draft resets.
  const addressFormErrors = useMemo(
    () =>
      (errors ?? [])
        .filter(e => e.path?.startsWith('address.'))
        .map(e => ({ ...e, path: e.path?.slice('address.'.length) ?? '' })),
    [errors]
  );

  const set = (field: keyof Omit<PartnerValue, 'address'>, val: string) => {
    setDraft(prev => ({ ...prev, [field]: val }));
    onDraftChange?.();
  };

  const handleAddressChange = (field: Parameters<typeof addressState.setField>[0], val: string) => {
    addressState.setField(field, val);
    onDraftChange?.();
  };

  const buildValue = (): PartnerValue => ({
    ...draft,
    address: toAddress(addressState.form)
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const next = buildValue();
      if (onCommit) {
        await onCommit(next);
      } else {
        onChange(next);
      }
    } catch (err) {
      if (onRegisterSave) throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (!onRegisterSave) return;
    onRegisterSave(() => handleSaveRef.current());
    return () => onRegisterSave(null);
  }, [onRegisterSave]);

  return (
    <div className='flex flex-col gap-sm w-full'>
      <InputWrapper label={t('name')} labelSize='sm'>
        <Input
          value={draft.name}
          onChange={e => set('name', e.target.value)}
          disabled={saving}
          data-hover-field={hoverField(prefix, 'name')}
        />
      </InputWrapper>

      <div className='grid grid-cols-3 gap-sm'>
        <InputWrapper label={t('companyId')} labelSize='sm'>
          <Input
            value={draft.companyId}
            onChange={e => set('companyId', e.target.value)}
            disabled={saving}
            data-hover-field={hoverField(prefix, 'companyId')}
          />
        </InputWrapper>
        <InputWrapper label={t('taxId')} labelSize='sm'>
          <Input
            value={draft.taxId}
            onChange={e => set('taxId', e.target.value)}
            disabled={saving}
            data-hover-field={hoverField(prefix, 'taxId')}
          />
        </InputWrapper>
        <InputWrapper label={t('vatId')} labelSize='sm'>
          <Input
            value={draft.vatId}
            onChange={e => set('vatId', e.target.value)}
            disabled={saving}
            data-hover-field={hoverField(prefix, 'vatId')}
          />
        </InputWrapper>
      </div>

      <div className='grid grid-cols-2 gap-sm'>
        <InputWrapper label={t('email')} labelSize='sm'>
          <Input
            value={draft.email}
            onChange={e => set('email', e.target.value)}
            disabled={saving}
            data-hover-field={hoverField(prefix, 'email')}
          />
        </InputWrapper>
        <InputWrapper label={t('phone')} labelSize='sm'>
          <Input
            value={draft.phone}
            onChange={e => set('phone', e.target.value)}
            disabled={saving}
            data-hover-field={hoverField(prefix, 'phone')}
          />
        </InputWrapper>
      </div>

      <div className='border-t border-border-base pt-sm'>
        <p className='text-xs font-medium text-foreground-muted mb-sm'>{tSections('address')}</p>
        <AddressForm
          value={addressState.form}
          onFieldChange={handleAddressChange}
          autoFocusStreet={false}
          errors={addressFormErrors}
          disabled={saving}
        />
      </div>

      {!onRegisterSave && (
        <div className='flex justify-end gap-sm pt-2xs'>
          <Button variant='ghost' size='sm' onClick={onCancel} disabled={saving}>
            {tActions('actions.cancel')}
          </Button>
          <Button variant='primary' size='sm' onClick={() => void handleSave()} disabled={saving}>
            {saving ? tActions('actions.saving') : tActions('actions.save')}
          </Button>
        </div>
      )}
    </div>
  );
}

PartnerInlineEditor.displayName = 'PartnerInlineEditor';
